import moment from 'moment-timezone';
import {
  type ComponentProps,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl';
import { AdStatus, CampaignMoreInfoModal, ForecastModal } from 'sonata-platform-components';
import {
  BudgetSpend,
  ButtonUI,
  ContextualMenu,
  PacingStatus,
  PlayButton,
  Status,
  showNotification,
  Tooltip,
  TooltipInteractionEnum,
  useFeatureUser,
  useQueryFetch
} from 'sonata-react-components';
import {
  deleteLineItem,
  duplicateLineItem,
  getEntityDiagnostic,
  getLineItemStatus,
  getLineItemsCalendarInfo,
  LineItem,
  pauseLineItem,
  resumeLineItem
} from 'sonata-static-storage/lib/entities';
import { State as LineItemState } from 'sonata-static-storage/lib/entities/lineItem/lineItem.types';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import { PacingStatusModal } from '@/components/common/PacingStatusModal';
import { PromptModal } from '@/components/common/PromptModal';
import { QuickReport } from '@/components/common/QuickReport';
import { getQuickReportMetrics } from '@/components/common/QuickReport/QuickReportChat/quickReportChart.helper';
import LineItemsEditButtons from '@/components/common/Table/LineItemsButtons/LineItemsEditButtons.tsx';
import { getDoohReportUrl } from '@/components/doohReport';
import { isDoohReportAvailable } from '@/components/doohReport/common/helpers/availability.helper';
import { isDriveToStoreReportAvailable } from '@/helpers/campaigns/driveToStoreAvailability.helper';
import { getPricingModelLabel } from '@/helpers/campaigns/pricingModel.helper';
import { normalizeCalendarInfo } from '@/helpers/common/date.helper';
import { createEntityStateLoader } from '@/helpers/entity/entityStateLoader.helper';
import { withPlatformV2OriginMarker } from '@/helpers/navigation/platformOrigin.helper';
import { isSecondaryActionClick } from '@/helpers/navigation/secondaryAction.helper';
import { adToValidate } from '@/services/common.service';
import { getScreenSourcesPublishersDictionary } from '@/services/common.service.ts';
import { getDoohReportAvailability } from '@/services/doohReport.service';
import { getLineItems } from '@/services/lineItem.service';
import { syncLineItem } from '@/services/lineItemId.service';
import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors.ts';
import { getUser } from '@/store/global/user/selectors.ts';
import { getUserRoles } from '@/store/global/userRoles/selectors.ts';
import { CampaignsStore, useStore as useCampaignStore } from '@/store/local/campaigns';
import Subtitle from '../ListComponents/Subtitle/Subtitle';
import { EntityNameTooltip } from '../Shared/EntityNameTooltip';
import { type EntityRowAction, EntityRowActions } from '../Shared/EntityTable/EntityRowActions';
import { EntityTable, type EntityTableColumn } from '../Shared/EntityTable/EntityTable';
import { EntityListStyle } from '../Shared/Shared.styled';
import { LineItemDays } from './LineItemDays/LineItemDays';
import { useLineItemsColumnLabels } from './lineItemsColumns';
import './lineItemsList.scss';

import { ChangelogModal } from '@/components/common/ChangelogModal';
import PauseConfirmationPrompt from '@/components/common/PauseConfirmationPrompt/PauseConfirmationPrompt';

import type {
  MultiFlightEntityState,
  MultiFlightEntityStateResponse,
  MultiFlightSyncErrorDetail
} from 'sonata-static-storage/lib/common/common.types';
import type { AdInterface } from 'sonata-static-storage/lib/schemas/ad/ad.schema';
import type { CampaignInterface } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';
import type { OptimizationModelInterface } from 'sonata-static-storage/lib/schemas/campaign/optimizationModel.schema';
import type { SelectedMultiflightEntity } from '@/store/local/campaigns';

type LIAction = EntityRowAction;

type TaptapListItem = {
  actions: LIAction[];
  budgetSpendProps: {
    entityId: string;
    getBudget: () => Promise<Awaited<ReturnType<typeof getEntityDiagnostic>>>;
    isInDraft: boolean;
  };
  durationProps: {
    entityId: string;
    getCalendarInfo: () => ReturnType<typeof getLineItemsCalendarInfo>;
    queryKey: string[];
    timezone?: string;
  };
  expandableContent: () => React.ReactNode;
  id: string;
  lastUpdateDate: string;
  lineItem: LineItem;
  pacingStatusProps: {
    entityId: string;
    getPacing: () => Promise<Awaited<ReturnType<typeof getEntityDiagnostic>>>;
    isInDraft: boolean;
    onClick?: () => void;
  };
  playButtonProps: {
    entityId: string;
    onClick: (action: 'toResume' | 'toPaused') => Promise<void>;
    onLoadState: () => Promise<{ state: MultiFlightEntityState; paused?: boolean }>;
    queryKey: string[];
  };
  pricingModel: string;
  statusProps: {
    entityId: string;
    getState: () => Promise<MultiFlightEntityStateResponse>;
    onSyncButtonClick: () => Promise<void>;
    queryKey: string[];
  };
  subtitleProps: { campaignPlanName: string; ioName: string; type: 'lineItem' };
  titleProps: { text: string };
};

type PauseConfirmationState = {
  isOpen: boolean;
  item: TaptapListItem | null;
};

type LIState = {
  disapprovalReasons: string[];
  /** @deprecated alias of updating, kept by the API until MTF-772 step 7 */
  launching?: boolean;
  paused: boolean;
  status: MultiFlightEntityState;
  state: LineItemState;
  syncError?: MultiFlightSyncErrorDetail;
  updating?: boolean;
};

const isUpdating = (liState: LIState) => Boolean(liState.updating ?? liState.launching);

const PLAY_BUTTON_STATES: MultiFlightEntityState[] = ['schedulePending', 'active', 'finished', 'paused'];

// Only the states the PlayButton acts on show it; the row's state loads lazily, so it waits for it
function PlayButtonGate({
  children,
  loadState
}: {
  children: ReactNode;
  loadState: TaptapListItem['playButtonProps']['onLoadState'];
}) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    let isMounted = true;
    loadState()
      .then(({ paused, state }) => isMounted && setIsVisible(Boolean(paused) || PLAY_BUTTON_STATES.includes(state)))
      .catch(() => undefined);
    return () => {
      isMounted = false;
    };
  }, [loadState]);

  return isVisible ? children : null;
}

type State = {
  changelogConfig?: { entityId: string; show: boolean; title: string; userId: string };
  entityPacingClicked?: string;
  forecastLineItem?: CampaignInterface;
  lineItemToDelete?: Record<string, unknown>;
  moreInfoConfig?: { entityId: string; optimizationModel: OptimizationModelInterface; show: boolean };
  pauseConfirmation: PauseConfirmationState;
  showDeletionPrompt: boolean;
  sortParams: { by: string; order: 'asc' | 'desc' };
};

/** Maps each sortable column id to the line item field the backend sorts by. */
const SORT_FIELD_BY_COLUMN: Record<string, string> = {
  duration: 'beginDate',
  lastUpdate: 'modified',
  name: 'name',
  pricingModel: 'bidModels.0.bid.pricingModel'
};

type Props = {
  cacheSeed: string;
  isColumnVisible: (columnId: string) => boolean;
  filters: Record<string, string | number | (string | number)[]>;
  onListChange: () => void;
  onSelectionChange: (selectedItems: SelectedMultiflightEntity[]) => void;
  selectionResetToken?: number;
};

type ReportingOption = {
  icon: string;
  id: string;
  onClick: (ev: MouseEvent) => void;
  subtitle?: string;
  text: string;
};

type ReportingItemParams = {
  extraData?: Record<string, any>;
  formatMessage: ReturnType<typeof useIntl>['formatMessage'];
  hasDoohReport: boolean;
  lineItem: LineItem;
  lineItemId: string;
  reportingHref: string;
  showReportingDooh: boolean;
};

function isDoohReportEligible(lineItem: LineItem, showReportingDooh: boolean): boolean {
  return (
    showReportingDooh &&
    lineItem.type === CampaignFamilyEnum.dooh &&
    isDoohReportAvailable(lineItem.endDate, lineItem.timezone)
  );
}

function getReportingItem({
  extraData,
  formatMessage,
  hasDoohReport,
  lineItem,
  lineItemId,
  reportingHref,
  showReportingDooh
}: ReportingItemParams): Extract<LIAction, { isCustomAction: true }> {
  const hasFootfallAttribution = lineItem.attributionSettings?.enabled;
  const hasDriveToStore = lineItem.attributionSettings?.driveToStore?.enabled;
  const poiId = extraData?.bestPoi as string | undefined;
  const advancedReportOptions: ReportingOption[] = [];

  if (hasFootfallAttribution && hasDriveToStore && isDriveToStoreReportAvailable(lineItem)) {
    const driveToStoreReportingHref = `reporting/driveToStore/lineItem/${lineItemId}`;
    if (poiId) {
      advancedReportOptions.push({
        icon: 'fa-kit-duotone fa-store-arrow-up',
        id: `li-single-footfall-${lineItemId}`,
        onClick: (ev: MouseEvent) => {
          if (isSecondaryActionClick(ev)) {
            window.open(`${driveToStoreReportingHref}?poiId=${poiId}`, '_blank', 'noopener,noreferrer');
          } else {
            window.location.assign(`${driveToStoreReportingHref}?poiId=${poiId}`);
          }
        },
        subtitle: formatMessage({
          defaultMessage: 'Attribution by unique POI',
          id: 'lineItems.list.reporting.singleFootfallReportSubtitle'
        }),
        text: formatMessage({
          defaultMessage: 'Single Store Report',
          id: 'lineItems.list.reporting.singleFootfallReport'
        })
      });
    }

    advancedReportOptions.push({
      icon: 'fa-solid fa-chart-mixed',
      id: `li-aggregated-report-${lineItemId}`,
      onClick: (ev: MouseEvent) => {
        if (isSecondaryActionClick(ev)) {
          window.open(driveToStoreReportingHref, '_blank', 'noopener,noreferrer');
        } else {
          window.location.assign(driveToStoreReportingHref);
        }
      },
      subtitle: formatMessage({
        defaultMessage: 'Attribution by all POIs',
        id: 'lineItems.list.reporting.aggregatedReportSubtitle'
      }),
      text: formatMessage({ defaultMessage: 'Multi-Store Report', id: 'lineItems.list.reporting.aggregatedReport' })
    });
  }

  if (isDoohReportEligible(lineItem, showReportingDooh) && hasDoohReport) {
    advancedReportOptions.push({
      icon: 'fa-kit fa-screen-play',
      id: `li-dooh-report-${lineItemId}`,
      onClick: () => window.open(getDoohReportUrl(lineItemId), '_blank', 'noopener,noreferrer'),
      subtitle: formatMessage({
        defaultMessage: 'Audience & screen metrics',
        id: 'lineItems.list.reporting.doohReportSubtitle'
      }),
      text: formatMessage({ defaultMessage: 'PrDOOH Report', id: 'lineItems.list.reporting.doohReport' })
    });
  }

  const sonataOption: ReportingOption = {
    icon: 'fa-solid fa-chart-column',
    id: `li-reporting-${lineItemId}`,
    onClick: (ev: MouseEvent) => {
      if (isSecondaryActionClick(ev)) {
        window.open(reportingHref, '_blank', 'noopener,noreferrer');
      } else {
        window.location.assign(reportingHref);
      }
    },
    subtitle: formatMessage({
      defaultMessage: 'Campaign metrics',
      id: 'lineItems.list.reporting.sonataV1ReportSubtitle'
    }),
    text: formatMessage({ defaultMessage: 'Delivery Report', id: 'lineItems.list.reporting.sonataV1Report' })
  };

  if (advancedReportOptions.length === 0) {
    return {
      content: (
        <Tooltip
          config={{
            component: <span>{sonataOption.text}</span>,
            interactions: [TooltipInteractionEnum.hover],
            placement: 'top',
            portal: true,
            strategy: 'fixed'
          }}
        >
          <span
            onContextMenu={ev => {
              ev.preventDefault();
              sonataOption.onClick(ev);
            }}
          >
            <ButtonUI
              icon={sonataOption.icon}
              id={sonataOption.id}
              onClick={sonataOption.onClick}
              shape="link"
              size="ultraSmall"
              type="primary"
            />
          </span>
        </Tooltip>
      ),
      id: sonataOption.id,
      isCustomAction: true
    };
  }

  return {
    content: (
      <Tooltip
        config={{
          component: <span>{formatMessage({ defaultMessage: 'Campaign Reports', id: 'list.reporting.tooltip' })}</span>,
          interactions: [TooltipInteractionEnum.hover],
          placement: 'top',
          portal: true,
          strategy: 'fixed'
        }}
      >
        <span>
          <ContextualMenu
            buttonConfig={{ icon: 'fa-solid fa-chart-mixed', shape: 'link', size: 'ultraSmall', type: 'primary' }}
            buttons={[[sonataOption], advancedReportOptions]}
            direction="vertical"
            id={`li-reporting-menu-${lineItemId}`}
            placement="bottom-start"
          />
        </span>
      </Tooltip>
    ),
    id: `li-reporting-menu-${lineItemId}`,
    isCustomAction: true
  };
}

const getDictionary = () => getScreenSourcesPublishersDictionary({});

// Reads the publishers dictionary itself, so the list rows (and fetchData) don't depend on it: a dictionary
// arriving after the first render would otherwise reload the whole list and repeat every row request.
// Fetched once and shared by every row (the app's queries never go stale).
function LineItemAdStatus(props: Omit<ComponentProps<typeof AdStatus>, 'publishersDictionary'>) {
  const { data: dictionary } = useQueryFetch(['getDictionary'], getDictionary);
  return <AdStatus {...props} publishersDictionary={dictionary} />;
}

export default function LineItemsList({
  cacheSeed,
  filters,
  isColumnVisible: isVisible,
  onListChange,
  onSelectionChange,
  selectionResetToken
}: Readonly<Props>) {
  const clearedSelection = useMemo<TaptapListItem[]>(() => [], [selectionResetToken]);
  const [
    {
      changelogConfig,
      entityPacingClicked,
      forecastLineItem,
      lineItemToDelete,
      moreInfoConfig,
      pauseConfirmation,
      showDeletionPrompt,
      sortParams
    },
    setState
  ] = useState<State>({
    changelogConfig: undefined,
    forecastLineItem: undefined,
    moreInfoConfig: undefined,
    pauseConfirmation: { isOpen: false, item: null },
    showDeletionPrompt: false,
    sortParams: {
      by: 'modified',
      order: 'desc'
    }
  });

  const handleSortChange = useCallback((columnId: string, direction: 'asc' | 'desc') => {
    const by = SORT_FIELD_BY_COLUMN[columnId];
    if (by) {
      setState(prevState => ({ ...prevState, sortParams: { by, order: direction } }));
    }
  }, []);

  const pauseConfirmCallbackRef = useRef<((confirmed: boolean) => void) | null>(null);

  const currencyInfo = useGlobalStore(getBaseCurrency);
  const user = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);
  const { id: userId = '' } = user.entity;
  const { formatMessage } = useIntl();
  const columnLabels = useLineItemsColumnLabels();
  const listId = 'lineItemsList';
  const { setHasLineItems } = useCampaignStore() as CampaignsStore;
  const moreInfoPermission = checkUserResourcePermissions('campaignListMoreInfo', user.toJSON(), userRoles);

  const showReportingV2 = useFeatureUser('reportingV2');
  const showReportingV2InV2 = useFeatureUser('reportingV2inV2');
  const showReportingDooh = useFeatureUser('reportingDooh');

  const isDisabled = (lineItem: Record<string, unknown>) => {
    const validaDateToDuplicate = moment.utc().startOf('day').subtract(6, 'month');
    const creationDate = moment.utc(lineItem.created as string | undefined);
    return !creationDate.isSameOrAfter(validaDateToDuplicate);
  };

  const updateAdsAdExchangesValidation = async (
    lineItem: Record<string, unknown>,
    adsAdExchangesValidation: { adId: string; adExchanges: unknown }[]
  ) => {
    const ads = lineItem.ads as AdInterface<string>[];
    const nonDeletedAds = ads.filter((ad: AdInterface<string>) => !ad.deleted);
    const updatedAd = nonDeletedAds.find((ad: AdInterface<string>) => ad._id === adsAdExchangesValidation[0].adId);
    if (updatedAd) {
      updatedAd.adExchanges = adsAdExchangesValidation[0].adExchanges as AdInterface<string>['adExchanges'];
    }
    const newLineItem = {
      ...lineItem,
      ads: ads.map((ad: AdInterface<string>) => (ad._id === updatedAd?._id ? updatedAd : ad))
    };
    // updateLineItem is not used in the new pattern — the old updateLineItem call is preserved via the callback
    return newLineItem;
  };

  const updateAdsAdExchangesValidationCallback = (
    exchangeCode: string,
    ad: AdInterface<string>,
    lineItem: Record<string, unknown>
  ) => {
    adToValidate(
      exchangeCode,
      ad,
      lineItem as Parameters<typeof adToValidate>[2],
      updateAdsAdExchangesValidation as Parameters<typeof adToValidate>[3],
      formatMessage
    );
  };

  const handleDelete = (lineItem: Record<string, unknown>) => {
    setState(prevState => ({
      ...prevState,
      lineItemToDelete: lineItem,
      showDeletionPrompt: true
    }));
  };

  const handleConfirmDeletion = async () => {
    try {
      await deleteLineItem(lineItemToDelete?._id as string);
      const { totalItems } = await getLineItems(userId, { itemsPerPage: '1', page: '1' });
      if (totalItems === 0) {
        setHasLineItems(false);
      }
      onListChange();
      setState(prevState => ({
        ...prevState,
        lineItemToDelete: undefined,
        showDeletionPrompt: false
      }));
      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'Line Item deleted successfully',
          id: 'lineItems.notifications.deleted'
        })
      );
    } catch {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Failure deleting the Line Item',
          id: 'lineItems.notifications.notDeleted'
        })
      );
    }
  };

  const handleCancelDeletion = () => {
    setState(prevState => ({
      ...prevState,
      lineItemToDelete: undefined,
      showDeletionPrompt: false
    }));
  };

  const handlePauseConfirm = () => {
    pauseConfirmCallbackRef.current?.(true);
    pauseConfirmCallbackRef.current = null;
    setState(prevState => ({
      ...prevState,
      pauseConfirmation: { isOpen: false, item: null }
    }));
  };

  const handlePauseCancel = () => {
    pauseConfirmCallbackRef.current?.(false);
    pauseConfirmCallbackRef.current = null;
    setState(prevState => ({
      ...prevState,
      pauseConfirmation: { isOpen: false, item: null }
    }));
  };

  const handleDuplicate = async (lineItem: Record<string, unknown>) => {
    try {
      await duplicateLineItem(lineItem._id as string);
      onListChange();
      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'Line Item duplicated successfully',
          id: 'lineItems.notifications.duplicated'
        })
      );
    } catch {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Failure duplicating the Line Item',
          id: 'lineItems.notifications.notDuplicated'
        })
      );
    }
  };

  const handleCloseModal = () => {
    setState(prevState => ({
      ...prevState,
      entityPacingClicked: undefined
    }));
  };

  const handleChangelogModalClose = () => {
    setState(prevState => ({
      ...prevState,
      changelogConfig: undefined
    }));
  };

  const handleMoreInfoModalClose = () => {
    setState(prevState => ({
      ...prevState,
      moreInfoConfig: undefined
    }));
  };

  const handleForecastModalClose = () => {
    setState(prevState => ({
      ...prevState,
      forecastLineItem: undefined
    }));
  };

  const handleSelectionChange = (items: TaptapListItem[]) => {
    const selectedLineItems: SelectedMultiflightEntity[] = items.map(item => ({
      id: item.id,
      isDriveToStoreEligible: Boolean(
        item.lineItem.attributionSettings?.enabled &&
          item.lineItem.attributionSettings?.driveToStore?.enabled &&
          isDriveToStoreReportAvailable(item.lineItem)
      ),
      lineItemIds: [item.id]
    }));
    onSelectionChange(selectedLineItems);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: cacheSeed is intentionally listed to trigger re-fetches on external invalidation
  const fetchData = useCallback(
    async (params: { page: number; pageSize: number }) => {
      const filterParams = Object.fromEntries(
        Object.entries(filters).map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : String(v)])
      );
      const requestParams = {
        includeExtraData: 'true',
        itemsPerPage: params.pageSize.toString(),
        page: params.page.toString(),
        sortBy: sortParams.by,
        sortOrder: sortParams.order,
        ...filterParams
      };

      try {
        const response = await getLineItems(userId, requestParams);
        // Each list load gets its own state cache, so rows show the state their loaders just fetched
        const loadedAt = Date.now().toString();

        const items: TaptapListItem[] = await Promise.all(
          (response?.items ?? []).map(async (lineItem: LineItem & { extraData?: Record<string, any> }) => {
            const entityDiagnosticPromise = getEntityDiagnostic(userId, lineItem._id as string, 'lineItem');
            // The list response carries no state: one request per row, shared by the pill, the PlayButton and its visibility
            const entityState = createEntityStateLoader(
              () => getLineItemStatus(lineItem._id as string) as Promise<LIState>
            );
            const getCalendarInfo = () =>
              getLineItemsCalendarInfo(userId, lineItem._id as string).then(normalizeCalendarInfo);
            const lineItemId = String(lineItem._id);
            let reportingHref = '';
            if (showReportingV2InV2) {
              reportingHref = `/reporting?cids=${lineItemId}`;
            } else if (showReportingV2) {
              reportingHref = withPlatformV2OriginMarker(
                `${taptap.config.sonataUrl}app.html#!/reporting-v2&q=${lineItemId}`
              );
            } else {
              reportingHref = withPlatformV2OriginMarker(
                `${taptap.config.sonataUrl}app.html#!/reporting/${lineItemId}`
              );
            }

            const hasDoohReport = isDoohReportEligible(lineItem, showReportingDooh)
              ? await getDoohReportAvailability(lineItemId)
                  .then(result => result.available)
                  .catch(() => false)
              : false;

            const extraData = lineItem.extraData;
            const actions: LIAction[] = [];

            actions.push(
              getReportingItem({
                extraData,
                formatMessage,
                hasDoohReport,
                lineItem,
                lineItemId,
                reportingHref,
                showReportingDooh
              })
            );

            const ads = lineItem.ads as AdInterface<string>[] | undefined;
            if (ads && ads.length > 0) {
              actions.push({
                content: (
                  <div className="adStatusContainer">
                    <LineItemAdStatus
                      ads={ads}
                      campaignId={lineItemId}
                      campaignName={lineItem.name as string}
                      campaignType={lineItem.type as string}
                      customIcon="fa-kit fa-regular-image-circle-check"
                      updateAdsAdExchangesValidationCallback={(exchangeCode: string, ad: AdInterface<string>) =>
                        updateAdsAdExchangesValidationCallback(exchangeCode, ad, lineItem)
                      }
                      user={user.toJSON?.()}
                      userRoles={userRoles}
                    />
                  </div>
                ),
                id: `li-adsStatus-${lineItemId}`,
                isCustomAction: true
              });
            }
            actions.push({
              content: <LineItemsEditButtons id={`li-edit-${lineItemId}`} lineItem={lineItem} />,
              id: `li-editContainer-${lineItemId}`,
              isCustomAction: true
            });
            const menuButtons: {
              disabled?: boolean;
              icon: string;
              id: string;
              onClick: () => void;
              size?: 'small' | 'ultraSmall';
              text: string;
            }[][] = [
              [
                {
                  disabled: !lineItem.completed,
                  icon: 'fa-regular fa-list',
                  id: `li-action-changelog-${lineItemId}`,
                  onClick: () =>
                    setState(prev => ({
                      ...prev,
                      changelogConfig: {
                        entityId: lineItemId,
                        show: true,
                        title: lineItem.name as string,
                        userId: lineItem.user as string
                      }
                    })),
                  text: formatMessage({ defaultMessage: 'Changelog', id: 'lineItems.list.buttons.changelog' })
                },
                {
                  icon: 'fa-regular fa-signal-bars',
                  id: `li-action-forecast-${lineItemId}`,
                  onClick: () =>
                    setState(prev => ({ ...prev, forecastLineItem: lineItem as unknown as CampaignInterface })),
                  text: formatMessage({ defaultMessage: 'Forecast', id: 'lineItems.list.buttons.forecast' })
                }
              ],
              [
                {
                  disabled: isDisabled(lineItem),
                  icon: 'fa fa-files-o',
                  id: `li-action-duplicate-${lineItemId}`,
                  onClick: () => handleDuplicate(lineItem),
                  text: formatMessage({ defaultMessage: 'Duplicate', id: 'lineItems.list.buttons.duplicate' })
                },
                {
                  icon: 'fa-regular fa-trash',
                  id: `li-action-delete-${lineItemId}`,
                  onClick: () => handleDelete(lineItem),
                  text: formatMessage({ defaultMessage: 'Delete', id: 'common.delete' })
                }
              ]
            ];

            if (moreInfoPermission) {
              menuButtons[0].push({
                disabled: !lineItem.optimizationModel,
                icon: 'fa-regular fa-chart-mixed',
                id: `li-action-metrics-${lineItemId}`,
                onClick: () =>
                  setState(prev => ({
                    ...prev,
                    moreInfoConfig: {
                      entityId: lineItemId,
                      optimizationModel: lineItem.optimizationModel as OptimizationModelInterface,
                      show: true
                    }
                  })),
                text: formatMessage({ defaultMessage: 'Opt. Model Metrics', id: 'lineItems.list.buttons.metrics' })
              });
            }

            actions.push({
              content: <ContextualMenu buttons={menuButtons} direction="vertical" id={`li-actions-${lineItemId}`} />,
              id: `li-more-actions-${lineItemId}`,
              isCustomAction: true
            });

            return {
              actions,
              budgetSpendProps: {
                entityId: lineItemId,
                getBudget: () => entityDiagnosticPromise,
                isInDraft: !lineItem.completed
              },
              durationProps: {
                entityId: lineItemId,
                getCalendarInfo,
                queryKey: [lineItemId],
                timezone: lineItem.timezone
              },
              expandableContent: () => (
                <QuickReport
                  entity={lineItem}
                  entityType="lineItem"
                  id={`multiflightExpandable-${lineItemId}`}
                  metrics={getQuickReportMetrics(lineItem.goals)}
                />
              ),
              id: lineItemId,
              lastUpdateDate: moment
                .utc((lineItem.modifiedDate || lineItem.created) as string | undefined)
                .format('DD/MM/YYYY'),
              lineItem,
              pacingStatusProps: {
                entityId: lineItemId,
                getPacing: () => entityDiagnosticPromise,
                isInDraft: !lineItem.completed,
                onClick: lineItem.completed
                  ? () => setState(prev => ({ ...prev, entityPacingClicked: lineItem._id as string }))
                  : undefined
              },
              playButtonProps: {
                entityId: lineItemId,
                onClick: async (action: 'toResume' | 'toPaused') => {
                  await (action === 'toPaused' ? pauseLineItem(lineItemId) : resumeLineItem(lineItemId));
                  entityState.invalidate();
                },
                onLoadState: async () => {
                  const result = await entityState.load();
                  return {
                    paused: result.paused,
                    state: result.paused ? 'paused' : (result.state as unknown as MultiFlightEntityState)
                  };
                },
                // Own key: it maps the state into a different shape than the Status pill
                queryKey: [loadedAt, 'playButton']
              },
              pricingModel: getPricingModelLabel(formatMessage, lineItem.bidModels[0]?.bid.pricingModel),
              statusProps: {
                entityId: lineItemId,
                getState: async () => {
                  const result = await entityState.load();
                  // The pill shows the real state; updating puts the sync badge on it, explained with syncError
                  return {
                    state: result.paused ? 'paused' : (result.state as unknown as MultiFlightEntityState),
                    updating: isUpdating(result),
                    ...(result.syncError && { syncError: result.syncError })
                  };
                },
                onSyncButtonClick: async () => {
                  try {
                    await syncLineItem(lineItemId);
                    entityState.invalidate();
                  } catch {
                    showNotification(
                      'error',
                      formatMessage({
                        defaultMessage: 'Line item failed to be synced, please try again',
                        id: 'lineItems.list.syncLineItem.error'
                      })
                    );
                  }
                },
                queryKey: [loadedAt]
              },
              subtitleProps: {
                campaignPlanName: extraData?.cpName || 'N/A',
                ioName: extraData?.ioName || 'N/A',
                type: 'lineItem' as const
              },
              titleProps: {
                text: lineItem.name as string
              }
            };
          })
        );

        return {
          data: items,
          page: params.page,
          pageSize: params.pageSize,
          total: response.totalItems,
          totalPages: Math.ceil(response.totalItems / params.pageSize)
        };
      } catch {
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'Failed to load line items',
            id: 'lineItems.fetchData.error'
          })
        );
        return { data: [], page: params.page, pageSize: params.pageSize, total: 0, totalPages: 0 };
      }
    },
    [userId, filters, cacheSeed, sortParams, formatMessage, user, userRoles, moreInfoPermission]
  );

  const columns: EntityTableColumn<TaptapListItem>[] = [
    {
      header: columnLabels.pricingModel,
      id: 'pricingModel',
      render: item => (
        <div className="multiflightPricingModel">
          <span>{item.pricingModel}</span>
        </div>
      ),
      sortable: true,
      width: 'var(--lineItemsList-col-pricing)'
    },
    {
      header: columnLabels.status,
      id: 'status',
      render: item => (
        <div className="multiflightStatus lineItemsList__statusCell">
          <Status
            entityId={item.statusProps.entityId}
            getState={item.statusProps.getState}
            onSyncButtonClick={item.statusProps.onSyncButtonClick}
            queryKey={item.statusProps.queryKey}
          />
          <div className="lineItemsList__playButtonSlot">
            <PlayButtonGate loadState={item.playButtonProps.onLoadState}>
              <PlayButton
                entityId={item.playButtonProps.entityId}
                onClick={async action => {
                  if (action === 'toPaused') {
                    const confirmed = await new Promise<boolean>(resolve => {
                      pauseConfirmCallbackRef.current = resolve;
                      setState(prevState => ({
                        ...prevState,
                        pauseConfirmation: { isOpen: true, item }
                      }));
                    });
                    if (!confirmed) {
                      return;
                    }
                  }
                  await item.playButtonProps.onClick(action);
                  onListChange();
                }}
                onLoadState={item.playButtonProps.onLoadState}
                queryKey={item.playButtonProps.queryKey}
                type="lineItem"
              />
            </PlayButtonGate>
          </div>
        </div>
      ),
      width: 'var(--lineItemsList-col-status)'
    },
    {
      header: columnLabels.duration,
      id: 'duration',
      render: item => (
        <div className="multiflightDuration">
          <LineItemDays
            countryCode={currencyInfo.countries?.[0]}
            entityId={item.durationProps.entityId}
            getCalendarInfo={item.durationProps.getCalendarInfo}
            queryKey={item.durationProps.queryKey}
            timezone={item.durationProps.timezone}
          />
        </div>
      ),
      sortable: true,
      width: 'var(--lineItemsList-col-duration)'
    },
    {
      header: columnLabels.lastUpdate,
      id: 'lastUpdate',
      render: item => <span className="multiflightLastUpdate">{item.lastUpdateDate}</span>,
      sortable: true,
      width: 'var(--lineItemsList-col-last-update)'
    },
    {
      align: 'right',
      header: columnLabels.budgetSpend,
      id: 'budgetSpend',
      render: item => (
        <span className="multiflightBudgetSpend">
          <BudgetSpend
            currencyInfo={currencyInfo}
            entityId={item.budgetSpendProps.entityId}
            getBudget={item.budgetSpendProps.getBudget}
            isInDraft={item.budgetSpendProps.isInDraft}
          />
        </span>
      ),
      width: 'var(--lineItemsList-col-budget)'
    }
  ];
  const visibleColumns = columns.filter(column => isVisible(column.id));

  return (
    <EntityListStyle className="lineItemsList">
      {showDeletionPrompt && (
        <PromptModal
          cancelCallback={handleCancelDeletion}
          confirmCallback={handleConfirmDeletion}
          texts={{
            buttons: {
              cancel: formatMessage({
                defaultMessage: 'Cancel',
                id: 'common.cancel'
              }),
              confirm: formatMessage({
                defaultMessage: 'Confirm',
                id: 'common.confirm'
              })
            },
            description: formatMessage(
              {
                defaultMessage:
                  "Deleting the line item {lineItemName} can't be undone and all associated information will be permanently lost.",
                id: 'lineItems.prompts.deletion.description'
              },
              {
                lineItemName: lineItemToDelete?.name as string | undefined
              }
            ) as string,
            title: formatMessage({
              defaultMessage: 'Are you sure you want to delete the selected line item?',
              id: 'lineItems.prompts.deletion.title'
            })
          }}
          variant="warning"
        />
      )}
      <EntityTable<TaptapListItem>
        actionsWidth="var(--lineItemsList-col-actions)"
        className="lineItemsList"
        columns={visibleColumns}
        defaultSelectedItems={clearedSelection}
        emptyStateTitle={formatMessage({
          defaultMessage: 'There is no data for the current search.',
          id: 'lineItems.list.emptyState.searchTitle'
        })}
        expandOnRowClick
        fetchData={fetchData}
        id={listId}
        initialPageSize={15}
        nameColumn={{
          header: formatMessage({ defaultMessage: 'Line Item Name', id: 'lineItems.list.toolbar.name' }),
          render: item => (
            <div className="multiflightTitle">
              <div className="multiflightTitle__main">
                <EntityNameTooltip name={item.titleProps.text}>
                  <span className="lineItemsList__titleText">{item.titleProps.text}</span>
                </EntityNameTooltip>
              </div>
              <div className="multiflightTitle__subtitle">
                <Subtitle
                  campaignPlanName={item.subtitleProps.campaignPlanName}
                  ioName={item.subtitleProps.ioName}
                  type={item.subtitleProps.type}
                />
              </div>
            </div>
          ),
          sortable: true
        }}
        onSelectionChange={handleSelectionChange}
        onSortChange={handleSortChange}
        renderActions={item => <EntityRowActions actions={item.actions} className="lineItemsList" />}
        renderLeading={item => (
          <PacingStatus
            entityId={item.pacingStatusProps.entityId}
            getPacing={item.pacingStatusProps.getPacing}
            isInDraft={item.pacingStatusProps.isInDraft}
            onClick={item.pacingStatusProps.onClick}
          />
        )}
      />
      {!!entityPacingClicked && (
        <PacingStatusModal
          currencyInfo={currencyInfo}
          entityId={entityPacingClicked}
          entityType="lineItem"
          isOpen={!!entityPacingClicked}
          onClose={handleCloseModal}
          userId={userId}
        />
      )}
      {changelogConfig?.show && (
        <ChangelogModal
          entity="lineItem"
          entityId={changelogConfig.entityId}
          onCloseModalCallback={handleChangelogModalClose}
          title={changelogConfig.title}
          userId={changelogConfig.userId}
        />
      )}
      {moreInfoConfig?.show && (
        <CampaignMoreInfoModal
          campaignId={moreInfoConfig.entityId}
          currencyInfo={currencyInfo}
          isModalOpened={moreInfoConfig.show}
          onModalClose={handleMoreInfoModalClose}
          optimizationModel={moreInfoConfig.optimizationModel}
        />
      )}
      {forecastLineItem && (
        <ForecastModal
          baseCurrency={currencyInfo.baseCurrency}
          campaign={forecastLineItem}
          isModalOpened={!!forecastLineItem}
          onModalClose={handleForecastModalClose}
          userId={userId}
        />
      )}
      <PauseConfirmationPrompt
        campaignPlanName={pauseConfirmation.item?.subtitleProps.campaignPlanName ?? ''}
        entityType="lineItem"
        ioName={pauseConfirmation.item?.subtitleProps.ioName}
        isOpen={pauseConfirmation.isOpen}
        lineItemName={pauseConfirmation.item?.titleProps.text}
        onCancel={handlePauseCancel}
        onConfirm={handlePauseConfirm}
      />
    </EntityListStyle>
  );
}
